import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Dialog } from "@mui/material";
import { CircleCheck } from "lucide-react";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { PageLayout } from "@/shared/components";
import { AddToCollection } from "@/shared/components/collections";
import type {
  FilterDefinition,
  SelectionOption,
} from "@/shared/components/ResourceView";
import {
  CreateSessionButton,
  SessionsList,
  useCoreSessionFilters,
  useSessionCrossIdFilters,
} from "@/shared/components/sessions";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { SessionRecord } from "@/shared/domain/sessions";
import { usePopupState } from "@/shared/hooks/usePopupState";

import { CompleteSessionDialog } from "./SessionDetail/CompleteSessionDialog";

/** Rows a bulk action targets, plus the callback that clears the selection on completion. */
interface PendingCollectionAdd {
  ids: string[];
  clearSelection: () => void;
}

/** Selected sessions to mark complete, plus the callback that clears the selection after. */
interface PendingCompletion {
  sessions: SessionRecord[];
  clearSelection: () => void;
}

/**
 * Top-level `/sessions` page. Renders the shared `SessionsList` with the
 * unscoped filter picker, a toolbar entry point into the create-session
 * flow, and bulk "Add to Collection" and "Mark complete" selection actions.
 */
export function SessionsPage() {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const coreFilters = useCoreSessionFilters(resolvedOrgId);
  const crossIdFilters = useSessionCrossIdFilters(resolvedOrgId);

  // Both cross-aggregate id rows are user-narrowable here because no
  // server-side scope is applied.
  const filterDefinitions = React.useMemo<FilterDefinition[]>(
    () => [
      ...coreFilters,
      crossIdFilters.datasetIdFilter,
      crossIdFilters.deviceIdFilter,
    ],
    [coreFilters, crossIdFilters],
  );

  const addToCollectionPopup = usePopupState<PendingCollectionAdd>();
  const { open: openAddToCollectionPopup } = addToCollectionPopup;
  const pendingAdd = addToCollectionPopup.metadata;

  const ingestionEnabled = useCheckFeatureFlag(
    "release-session-ingestion",
    resolvedOrgId,
  );
  // Kept apart from `completionOpen` so the dialog keeps its sessions while
  // it animates closed.
  const [pendingCompletion, setPendingCompletion] =
    React.useState<PendingCompletion | null>(null);
  const [completionOpen, setCompletionOpen] = React.useState(false);

  const selectionOptions = React.useMemo(() => {
    const options: SelectionOption<SessionRecord>[] = [
      {
        name: "Add to Collection",
        icon: <PlaylistAddIcon fontSize="small" />,
        action: ({ selected, clearSelection }) => {
          openAddToCollectionPopup({
            metadata: {
              ids: selected.map((s) => s.session_id),
              clearSelection,
            },
          });
        },
      },
    ];
    if (ingestionEnabled) {
      options.push({
        name: "Mark complete",
        icon: <CircleCheck size={18} aria-hidden="true" />,
        action: ({ selected, clearSelection }) => {
          setPendingCompletion({ sessions: selected, clearSelection });
          setCompletionOpen(true);
        },
      });
    }
    return options;
  }, [openAddToCollectionPopup, ingestionEnabled]);

  return (
    <PageLayout title="Roboto - Sessions" headerContentLeft="Sessions">
      <SessionsList
        filterDefinitions={filterDefinitions}
        noRowsContent="No sessions created yet"
        actions={<CreateSessionButton />}
        selectionOptions={selectionOptions}
      />
      <Dialog
        open={addToCollectionPopup.isOpen}
        onClose={addToCollectionPopup.close}
      >
        <AddToCollection
          selected={{
            type: "Session",
            items: new Set(pendingAdd?.ids ?? []),
          }}
          onCancel={addToCollectionPopup.close}
          onSuccess={() => pendingAdd?.clearSelection()}
        />
      </Dialog>
      <CompleteSessionDialog
        sessions={pendingCompletion?.sessions ?? []}
        open={completionOpen}
        onOpenChange={setCompletionOpen}
        onCompleted={() => pendingCompletion?.clearSelection()}
      />
    </PageLayout>
  );
}
